import React from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "@material-ui/core/Card";
import Chip from "@material-ui/core/Chip";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
    sectionTitle: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#555",
        borderBottom: "1px solid #e0e0e0",
        paddingBottom: "6px",
        marginBottom: "10px",
    },
    label: {
        fontSize: "11px",
        color: "#888",
        marginBottom: "2px",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
    },
    value: {
        fontSize: "13px",
        fontWeight: 500,
        color: "#333",
        marginBottom: "12px",
    },
    ancNumber: {
        fontSize: "22px",
        fontWeight: 700,
        color: "#1976d2",
        letterSpacing: "1px",
    },
}));

const Field = ({ label, value, classes }) => (
    <div>
        <div className={classes.label}>{label}</div>
        <div className={classes.value}>{value || "—"}</div>
    </div>
);

const SectionTitle = ({ children, classes }) => (
    <h6 className={classes.sectionTitle}>{children}</h6>
);

const formatDate = (d) =>
    d
        ? new Date(d).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
          })
        : "—";

const labelify = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * ANCBookingDetails — Read-only summary of an existing ANC booking.
 * Shown in place of the booking form when a patient already has an active booking.
 */
const ANCBookingDetails = ({ existingBooking, currentGestationalAge, onEditBooking }) => {
    const classes = useStyles();

    if (!existingBooking) return null;

    const {
        anc_number,
        booking_date,
        lnmp,
        edd,
        gestational_age_at_booking,
        gravida,
        parity,
        height_cm,
        pre_pregnancy_weight_kg,
        blood_group,
        rhesus_factor,
        allergies,
        medical_history,
        surgical_history,
        previous_pregnancies,
        marital_status,
        education_level,
        occupation,
        next_of_kin_name,
        next_of_kin_phone,
        next_of_kin_relationship,
        contacts_done = 0,
        status,
    } = existingBooking;

    const TOTAL_CONTACTS = 8;

    const activeMedical = medical_history
        ? Object.entries(medical_history)
              .filter(([, v]) => v)
              .map(([k]) => labelify(k))
        : [];

    const activeSurgical = surgical_history
        ? Object.entries(surgical_history)
              .filter(([, v]) => v)
              .map(([k]) => labelify(k))
        : [];

    const rhesusLabel =
        rhesus_factor === "positive"
            ? "Positive (+)"
            : rhesus_factor === "negative"
            ? "Negative (−)"
            : rhesus_factor || "—";

    return (
        <div>
            {/* ── Header card ─────────────────────────────────────────── */}
            <Card style={{ padding: "16px", marginBottom: "12px", borderLeft: "4px solid #1976d2" }}>
                <Row className="align-items-center">
                    <Col sm={5}>
                        <div className={classes.label}>ANC Number</div>
                        <div className={classes.ancNumber}>{anc_number || "—"}</div>
                        <div className={classes.label} style={{ marginTop: "4px" }}>
                            Booked: {formatDate(booking_date)}
                        </div>
                    </Col>
                    <Col sm={4}>
                        <div className={classes.label}>Contact Progress</div>
                        <div style={{ fontSize: "20px", fontWeight: 700, color: "#4caf50" }}>
                            {contacts_done}
                            <span style={{ fontSize: "13px", color: "#888", fontWeight: 400 }}>
                                {" "}/ {TOTAL_CONTACTS} contacts
                            </span>
                        </div>
                        <div style={{ display: "flex", gap: "5px", marginTop: "6px" }}>
                            {Array.from({ length: TOTAL_CONTACTS }).map((_, i) => (
                                <span
                                    key={i}
                                    title={`Contact ${i + 1}`}
                                    style={{
                                        width: "14px",
                                        height: "14px",
                                        borderRadius: "50%",
                                        display: "inline-block",
                                        backgroundColor: i < contacts_done ? "#4caf50" : "#e0e0e0",
                                        border:
                                            "2px solid " +
                                            (i < contacts_done ? "#388e3c" : "#bdbdbd"),
                                    }}
                                />
                            ))}
                        </div>
                    </Col>
                    <Col sm={3} className="text-right">
                        <div className={classes.label}>Status</div>
                        <Chip
                            label={status || "active"}
                            size="small"
                            style={{
                                backgroundColor: "#e8f5e9",
                                color: "#2e7d32",
                                fontWeight: 600,
                                textTransform: "capitalize",
                            }}
                        />
                        <div style={{ marginTop: "12px" }}>
                            <button
                                type="button"
                                className="btn btn-sm sms-info-btn"
                                onClick={onEditBooking}
                            >
                                Edit Booking
                            </button>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* ── Clinical Information ─────────────────────────────────── */}
            <Card style={{ padding: "16px", marginBottom: "12px" }}>
                <SectionTitle classes={classes}>Clinical Information</SectionTitle>
                <Row>
                    <Col sm={6} md={3}>
                        <Field label="LNMP" value={formatDate(lnmp)} classes={classes} />
                    </Col>
                    <Col sm={6} md={3}>
                        <Field label="Expected Date of Delivery" value={formatDate(edd)} classes={classes} />
                    </Col>
                    <Col sm={6} md={3}>
                        <Field
                            label="GA at Booking"
                            value={
                                gestational_age_at_booking != null
                                    ? `${gestational_age_at_booking} weeks`
                                    : "—"
                            }
                            classes={classes}
                        />
                    </Col>
                    <Col sm={6} md={3}>
                        <Field
                            label="Current Gestational Age"
                            value={currentGestationalAge?.display || "—"}
                            classes={classes}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col sm={6} md={3}>
                        <Field label="Gravida" value={gravida} classes={classes} />
                    </Col>
                    <Col sm={6} md={3}>
                        <Field label="Parity" value={parity} classes={classes} />
                    </Col>
                    <Col sm={6} md={3}>
                        <Field
                            label="Height"
                            value={height_cm ? `${height_cm} cm` : "—"}
                            classes={classes}
                        />
                    </Col>
                    <Col sm={6} md={3}>
                        <Field
                            label="Pre-pregnancy Weight"
                            value={pre_pregnancy_weight_kg ? `${pre_pregnancy_weight_kg} kg` : "—"}
                            classes={classes}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col sm={6} md={3}>
                        <Field label="Blood Group" value={blood_group} classes={classes} />
                    </Col>
                    <Col sm={6} md={3}>
                        <Field label="Rhesus Factor" value={rhesusLabel} classes={classes} />
                    </Col>
                    <Col sm={6} md={6}>
                        <Field label="Allergies" value={allergies} classes={classes} />
                    </Col>
                </Row>
            </Card>

            {/* ── Medical & Surgical History ───────────────────────────── */}
            {(activeMedical.length > 0 || activeSurgical.length > 0) && (
                <Card style={{ padding: "16px", marginBottom: "12px" }}>
                    <SectionTitle classes={classes}>Medical & Surgical History</SectionTitle>
                    {activeMedical.length > 0 && (
                        <div className="mb-3">
                            <div className={classes.label}>Medical Conditions</div>
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                    marginTop: "6px",
                                }}
                            >
                                {activeMedical.map((item) => (
                                    <Chip
                                        key={item}
                                        label={item}
                                        size="small"
                                        style={{
                                            backgroundColor: "#fff3e0",
                                            color: "#e65100",
                                            fontSize: "12px",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {activeSurgical.length > 0 && (
                        <div>
                            <div className={classes.label}>Surgical History</div>
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                    marginTop: "6px",
                                }}
                            >
                                {activeSurgical.map((item) => (
                                    <Chip
                                        key={item}
                                        label={item}
                                        size="small"
                                        style={{
                                            backgroundColor: "#e3f2fd",
                                            color: "#1565c0",
                                            fontSize: "12px",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {/* ── Previous Pregnancies ─────────────────────────────────── */}
            {previous_pregnancies && previous_pregnancies.length > 0 && (
                <Card style={{ padding: "16px", marginBottom: "12px" }}>
                    <SectionTitle classes={classes}>Previous Pregnancies</SectionTitle>
                    <div style={{ overflowX: "auto" }}>
                        <table className="table table-sm table-bordered" style={{ fontSize: "12px" }}>
                            <thead className="thead-light">
                                <tr>
                                    <th>#</th>
                                    <th>Year</th>
                                    <th>GA (wks)</th>
                                    <th>Mode</th>
                                    <th>Place</th>
                                    <th>Weight (kg)</th>
                                    <th>Sex</th>
                                    <th>Outcome</th>
                                    <th>Complications</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previous_pregnancies.map((p, i) => (
                                    <tr key={i}>
                                        <td>{p.pregnancy_number || i + 1}</td>
                                        <td>{p.year || "—"}</td>
                                        <td>{p.gestational_age_at_delivery || "—"}</td>
                                        <td>{p.mode_of_delivery || "—"}</td>
                                        <td>{p.place_of_delivery || "—"}</td>
                                        <td>{p.birth_weight || "—"}</td>
                                        <td>{p.sex || "—"}</td>
                                        <td>{p.outcome || "—"}</td>
                                        <td>{p.complications || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* ── Demographics & Next of Kin ───────────────────────────── */}
            <Card style={{ padding: "16px", marginBottom: "12px" }}>
                <SectionTitle classes={classes}>Demographics & Next of Kin</SectionTitle>
                <Row>
                    <Col sm={6} md={4}>
                        <Field
                            label="Marital Status"
                            value={
                                marital_status
                                    ? marital_status.charAt(0).toUpperCase() +
                                      marital_status.slice(1)
                                    : "—"
                            }
                            classes={classes}
                        />
                    </Col>
                    <Col sm={6} md={4}>
                        <Field
                            label="Education Level"
                            value={education_level ? labelify(education_level) : "—"}
                            classes={classes}
                        />
                    </Col>
                    <Col sm={6} md={4}>
                        <Field label="Occupation" value={occupation} classes={classes} />
                    </Col>
                </Row>
                <Row>
                    <Col sm={6} md={4}>
                        <Field label="Next of Kin" value={next_of_kin_name} classes={classes} />
                    </Col>
                    <Col sm={6} md={4}>
                        <Field label="NOK Phone" value={next_of_kin_phone} classes={classes} />
                    </Col>
                    <Col sm={6} md={4}>
                        <Field
                            label="Relationship"
                            value={next_of_kin_relationship ? labelify(next_of_kin_relationship) : "—"}
                            classes={classes}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default ANCBookingDetails;
